import { useState } from "react";
import { Hexagon, Boxes, Users } from "lucide-react";

const PERSONAS = [
  {
    key: "hobbyist",
    icon: Hexagon,
    eyebrow: "First-year hobbyist",
    headline: "Stay on top of your first hive without losing your mind.",
    body: "You don't need a spreadsheet, you need a friendly companion that asks the right questions. Our Smart Calendar walks you through your first season — when to inspect, when to treat, when to harvest — with the reasoning behind every nudge.",
    bullets: [
      "Free for as long as you have one yard",
      "Members-only beekeeping guide with starter content",
      "Community forum to ask anything",
      "AI Diagnose to triage that worrying frame",
    ],
  },
  {
    key: "sideliner",
    icon: Boxes,
    eyebrow: "Sideliner with 5–50 hives",
    headline: "Manage twenty hives like you manage one.",
    body: "When you're running a real operation, every minute saved in the field compounds. Voice journaling captures what you see, multi-apiary support keeps yards separate, and side-by-side compare tells you which colonies are punching above their weight.",
    bullets: [
      "Unlimited hives across unlimited yards",
      "Voice-to-Journal — dictate while you inspect",
      "CSV exports + printable annual reports for taxes",
      "Treatment tracker with withdrawal-period reminders",
    ],
  },
  {
    key: "club",
    icon: Users,
    eyebrow: "Bee club or shared apiary",
    headline: "Many keepers, one shared logbook.",
    body: "Co-managing a teaching apiary or club hives? Invite every member with the right access level, share one source of truth for every inspection, and never have the \"wait, did anyone treat this hive?\" conversation again.",
    bullets: [
      "Unlimited co-keeper invites on Team",
      "Apiary-wide collaboration & shared journals",
      "Audit log of every change",
      "Priority email support",
    ],
  },
];

export default function UseCases() {
  const [active, setActive] = useState("hobbyist");
  const current = PERSONAS.find((p) => p.key === active) || PERSONAS[0];

  return (
    <section
      id="spy-use-cases"
      className="bg-stone-900 text-amber-50"
      data-testid="landing-use-cases"
    >
      <div className="max-w-6xl mx-auto px-6 py-24 sm:py-32">
        <header className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-300 mb-3">Whether you're…</div>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-amber-50">
            Built for every kind of beekeeper.
          </h2>
        </header>

        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="use-case-tabs">
          {PERSONAS.map((p) => {
            const isActive = active === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setActive(p.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-amber-400 text-stone-950"
                    : "bg-stone-800 text-amber-200 hover:bg-stone-700"
                }`}
                data-testid={`use-case-tab-${p.key}`}
              >
                <p.icon className="w-4 h-4" />
                {p.eyebrow}
              </button>
            );
          })}
        </div>

        <div
          key={current.key}
          className="grid md:grid-cols-2 gap-10 items-center bg-stone-800/60 border border-stone-700 rounded-3xl p-8 sm:p-10"
          data-testid={`use-case-panel-${current.key}`}
        >
          <div>
            <div className="w-14 h-14 rounded-2xl bg-amber-400 text-stone-950 grid place-items-center mb-4">
              <current.icon className="w-7 h-7" />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-light text-amber-50 leading-tight">
              {current.headline}
            </h3>
          </div>
          <div className="space-y-4">
            <p className="text-stone-300 leading-relaxed">{current.body}</p>
            <ul className="space-y-2">
              {current.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-stone-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
